import React, { useEffect, useState, useCallback, useMemo } from 'react';
import StorageFactory from '../../Util/StorageFactory';
import {
  ACCESS_RESTRICTED_MESSAGE,
  SESSION_USER_ID_KEY,
  SESSION_USER_ROLES_PERMISSIONS,
} from '../../Util/VMConstants';
import { API_GET_SUBMITTED_FEEDBACK_LIST } from '../../Util/URLConstants';
import { toast } from 'react-toastify';
import {
  Box,
  Tooltip,
  TextField,
  Button,
  Stack,
  Chip,
  IconButton,
  Drawer,
  Divider,
  Avatar,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { dataGridStyle, VM_Colors } from '../../Styles/VMStyles';
import TitleWithAddButton from '../Home/TitleWithAddButton';
import AssignFeedback from './AssignFeedback';
import MUITypography from '../../Components/MUITypography';
import {
  checkIsPermissionExist,
  toTitleCase,
  updateColorBasedOnStatus,
} from '../../Util/UtilHelper';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import dayjs from 'dayjs';
import NoPermission from '../../Components/NoPermission';
import ReusableDataGrid from '../../Components/ReusableDataGrid';

const feedbackGridSx = {
  ...dataGridStyle,
  border: 'none',
  '& .MuiDataGrid-root': { border: 'none' },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: VM_Colors.primary,
    minHeight: '48px !important',
    maxHeight: '48px !important',
  },
  '& .MuiDataGrid-row:nth-of-type(even)': {
    backgroundColor: VM_Colors.white2,
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: 'rgba(30, 58, 138, 0.06) !important',
  },
  '& .MuiDataGrid-cell': {
    ...dataGridStyle['& .MuiDataGrid-cell'],
    alignItems: 'center',
    py: 1,
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: `1px solid ${VM_Colors.light_gray}`,
    minHeight: 52,
  },
};

function VMFeedback() {
  const sessionFactory = new StorageFactory('session');
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);
  const VM_PERMISSIONS = sessionFactory.get(SESSION_USER_ROLES_PERMISSIONS);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  const [isCreateFeedbackEnable, setIsCreateFeedbackEnable] = useState(false);

  // Drawer state
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [copiedRowId, setCopiedRowId] = useState(null);
  const [statusCounts, setStatusCounts] = useState({
    All: 0,
    Completed: 0,
    Incompleted: 0,
  });

  const filterStatuses = ['All', 'Completed', 'Incompleted'];

  // Fetch Data
  const fetchData = useCallback(async (pageOverride) => {
    const activePage = pageOverride ?? page;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        user_id: userID,
        page: activePage,
        no_of_rows: pageSize,
        status: statusFilter,
      });

      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);

      const res = await fetch(`${API_GET_SUBMITTED_FEEDBACK_LIST}?${params}`);
      const resp = await res.json();

      if (resp.status_code === 200 && Array.isArray(resp.data)) {
        const formatted = resp.data.map((fb, idx) => ({
          id: fb.id,
          s_no: activePage * pageSize + idx + 1,
          name: fb.recipient_name,
          phone_no: fb.phone_no,
          created_by: fb.created_by || null,
          created_by_name: fb.created_by?.name || '',
          created_date: fb.created_date,
          feedback: fb.feedback,
          is_completed: fb.is_completed,
          feedback_link: fb.feedback_link,
          referral: fb.referral || [],
        }));

        setRows(formatted);
        setTotalCount(resp.total_records || 0);
      } else {
        toast.error(resp.message || 'Invalid response');
        setRows([]);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [userID, page, pageSize, statusFilter, fromDate, toDate]);

  const fetchStatusCounts = useCallback(async () => {
    try {
      const results = await Promise.all(
        filterStatuses.map(async (status) => {
          const params = new URLSearchParams({
            user_id: userID,
            page: '0',
            no_of_rows: '1',
            status,
          });
          if (fromDate) params.append('from_date', fromDate);
          if (toDate) params.append('to_date', toDate);

          const res = await fetch(`${API_GET_SUBMITTED_FEEDBACK_LIST}?${params}`);
          const resp = await res.json();
          const count = resp.status_code === 200 ? (resp.total_records || 0) : 0;
          return [status, count];
        })
      );
      setStatusCounts(Object.fromEntries(results));
    } catch (err) {
      console.error('Error fetching feedback status counts:', err);
    }
  }, [userID, fromDate, toDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchStatusCounts();
  }, [fetchStatusCounts]);

  const refreshFeedbackList = useCallback(() => {
    if (page !== 0) {
      setPage(0);
    } else {
      fetchData(0);
    }
    fetchStatusCounts();
  }, [page, fetchData, fetchStatusCounts]);

  const handleCopyLink = async (row) => {
    if (!row?.feedback_link) {
      toast.error('No feedback link available');
      return;
    }
    try {
      await navigator.clipboard.writeText(row.feedback_link);
      setCopiedRowId(row.id);
      setTimeout(() => {
        setCopiedRowId((prev) => (prev === row.id ? null : prev));
      }, 2000);
    } catch (err) {
      console.error('Copy failed:', err);
      toast.error('Failed to copy link');
    }
  };

  // Share
  const handleShare = (row) => {
    const message = `Feedback Request\n\n${row.feedback_link}`;

    if (navigator.share) {
      navigator.share({
        title: 'Feedback',
        text: message,
        url: row.feedback_link,
      });
    } else {
      navigator.clipboard.writeText(message);
      toast.success('Copied!');
    }
  };

  // View Drawer
  const handleView = (row) => {
    setSelectedFeedback(row);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setSelectedFeedback(null);
    setIsDrawerOpen(false);
  };

  const renderTextCell = (value) => (
    <Typography
      variant="body2"
      sx={{
        lineHeight: 1.4,
        wordBreak: 'break-word',
        color: value ? 'text.primary' : 'text.disabled',
      }}
    >
      {value || '—'}
    </Typography>
  );

  const columns = useMemo(
    () => [
      {
        field: 's_no',
        headerName: 'S.No',
        width: 64,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {params.value}
          </Typography>
        ),
      },

      {
        field: 'name',
        headerName: 'Recipient Name',
        flex: 1.2,
        minWidth: 140,
        sortable: false,
        renderCell: (params) =>
          renderTextCell(params.value ? toTitleCase(params.value) : ''),
      },
      {
        field: 'phone_no',
        headerName: 'Phone No',
        flex: 0.9,
        minWidth: 120,
        sortable: false,
        renderCell: (params) => renderTextCell(params.value),
      },
      // {
      //   field: 'is_completed',
      //   headerName: 'Status',
      //   width: 118,
      //   sortable: false,
      //   renderCell: (params) => (
      //     <Chip
      //       label={params.value ? 'Completed' : 'Pending'}
      //       size="small"
      //       sx={{
      //         height: 26,
      //         fontWeight: 600,
      //         fontSize: '0.75rem',
      //         bgcolor: params.value ? '#e8f5e9' : '#fff3e0',
      //         color: params.value ? '#2e7d32' : '#e65100',
      //       }}
      //     />
      //   ),
      // },

      {
        field: 'feedback',
        headerName: 'Feedback Summary',
        flex: 1.4,
        minWidth: 160,
        sortable: false,
        renderCell: (params) => {
          const entries = Object.entries(params.value || {});
          if (entries.length === 0) {
            return (
              <Typography variant="body2" color="text.disabled" fontStyle="italic">
                Awaiting response
              </Typography>
            );
          }
          return (
            <Box sx={{ py: 0.25 }}>
              {entries.slice(0, 2).map(([key, val]) => (
                <Typography key={key} variant="body2" sx={{ lineHeight: 1.45 }}>
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {key}:
                  </Box>{' '}
                  {val || '—'}
                </Typography>
              ))}
              {entries.length > 2 && (
                <Typography variant="caption" color="text.secondary">
                  +{entries.length - 2} more
                </Typography>
              )}
            </Box>
          );
        },
      },

      {
        field: 'created_date',
        headerName: 'Date',
        width: 168,
        sortable: false,
        renderCell: (params) =>
          renderTextCell(
            params.value ? dayjs(params.value).format('DD MMM YYYY, hh:mm A') : ''
          ),
      },

      {
        field: 'view',
        headerName: 'View',
        width: 72,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Tooltip title="View details">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleView(params.row)}
              aria-label="View feedback details"
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 100,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5} justifyContent="center">
            {params.row.feedback_link && (
              <Tooltip
                title={copiedRowId === params.row.id ? 'Link copied' : 'Copy link'}
              >
                <IconButton
                  size="small"
                  color={copiedRowId === params.row.id ? 'success' : 'primary'}
                  onClick={() => handleCopyLink(params.row)}
                  aria-label="Copy feedback link"
                >
                  {copiedRowId === params.row.id ? (
                    <CheckIcon fontSize="small" />
                  ) : (
                    <ContentCopyIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
            {!params.row.is_completed && params.row.feedback_link && (
              <Tooltip title="Share link">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleShare(params.row)}
                  aria-label="Share feedback link"
                >
                  <ShareIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    [copiedRowId]
  );

  return (
    <Box ml={{ lg: 5 }} p={2}>
      {isCreateFeedbackEnable ? (
        <AssignFeedback
          onClose={() => setIsCreateFeedbackEnable(false)}
          onCreated={refreshFeedbackList}
        />
      ) : (
        <>
          <TitleWithAddButton
            title="Feedback Management"
            onAddClick={() => setIsCreateFeedbackEnable(true)}
            buttonLabel="Create Feedback"
            isButtonDisplay={checkIsPermissionExist(VM_PERMISSIONS, 'VM026')}
          />

          {/* Filters (left) + status pills (right) — single row */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: { xs: 'wrap', lg: 'nowrap' },
              my: 2,
              mb: 2,
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ flex: { xs: '1 1 100%', lg: '0 1 auto' } }}
            >
              <TextField
                type="date"
                size="small"
                label="From"
                value={fromDate}
                placeholder="From Date"
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
              <TextField
                type="date"
                size="small"
                label="To"
                value={toDate}
                placeholder="To Date"
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
              <Button
                variant="contained"
                onClick={() => {
                  setPage(0);
                  fetchData(0);
                  fetchStatusCounts();
                }}
              >
                Apply
              </Button>
              <Button
                onClick={() => {
                  setFromDate('');
                  setToDate('');
                  setPage(0);
                  fetchData(0);
                  fetchStatusCounts();
                }} variant="outlined"
                sx={{
                  minWidth: 90,
                  flex: { xs: 1, sm: "unset" },
                }}
              >
                Clear
              </Button>
            </Stack>

            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                justifyContent: { xs: 'flex-start', lg: 'flex-end' },
                flex: { xs: '1 1 100%', lg: '0 0 auto' },
                ml: { lg: 'auto' },
              }}
            >
              {filterStatuses.map((status) => {
                const statusColor = updateColorBasedOnStatus(status);
                const isSelected = statusFilter === status;
                return (
                  <Chip
                    key={status}
                    label={`${status} (${statusCounts[status] ?? 0})`}
                    clickable
                    onClick={() => {
                      setStatusFilter(status);
                      setPage(0);
                    }}
                    sx={{
                      bgcolor: `${statusColor} !important`,
                      color: '#fff !important',
                      fontWeight: isSelected ? 700 : 500,
                      opacity: isSelected ? 1 : 0.92,
                      transform: isSelected ? 'scale(1.03)' : 'none',
                      transition: 'transform 0.15s ease, opacity 0.15s ease',
                      '&:hover': {
                        bgcolor: `${statusColor} !important`,
                        opacity: 1,
                      },
                      '&:active': {
                        bgcolor: `${statusColor} !important`,
                      },
                      '&.Mui-focusVisible': {
                        bgcolor: `${statusColor} !important`,
                      },
                      '& .MuiChip-label': {
                        color: '#fff',
                      },
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          {/* Grid */}
          {checkIsPermissionExist(VM_PERMISSIONS, 'VM027') ? (
            <Box
              sx={{
                bgcolor: '#fff',
                borderRadius: 2,
                border: `1px solid ${VM_Colors.light_gray}`,
                overflow: 'hidden',
                boxShadow: '0 1px 4px rgba(15, 23, 42, 0.08)',
              }}
            >
              <ReusableDataGrid
                rows={rows}
                columns={columns}
                loading={loading}
                rowCount={totalCount}
                paginationMode="server"
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(0);
                }}
                pageSizeOptions={[5, 10, 20]}
                autoHeight
                getRowHeight={() => 56}
                customSx={feedbackGridSx}
              />
            </Box>
            // <MUIDataGrid
            //   rows={rows}
            //   columns={columns}
            //   loading={loading}
            //   page={page}
            //   pageSize={pageSize}
            //   rowCount={totalCount}
            //   paginationMode="server"
            //   onPageChange={setPage}
            //   onPageSizeChange={(size) => {
            //     setPageSize(size);
            //     setPage(0);
            //   }}
            //   rowsPerPageOptions={[5, 10, 20]}
            //   sx={dataGridStyle}
            // />
          ) : (
            <NoPermission message={ACCESS_RESTRICTED_MESSAGE} />
          )}
        </>
      )}

      {/* Drawer */}
<Drawer
  anchor="right"
  open={isDrawerOpen}
  onClose={handleCloseDrawer}
  PaperProps={{
    sx: {
      width: { xs: '100%', sm: 480, md: 540 },
    },
  }}
>
  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
    
    {/* Header */}
    <Box sx={{ p: 3, borderBottom: '1px solid #eee' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ bgcolor: VM_Colors.primary }}>
            {selectedFeedback?.name?.charAt(0) || 'U'}
          </Avatar>

          <Box>
            <MUITypography variant="h6">
              {selectedFeedback?.name || '—'}
            </MUITypography>

            <Chip
              label={
                selectedFeedback?.is_completed
                  ? 'Completed'
                  : 'Pending'
              }
              size="small"
              sx={{
                mt: 0.5,
                backgroundColor: selectedFeedback?.is_completed
                  ? '#4caf50'
                  : '#ff9800',
                color: '#fff',
              }}
            />
          </Box>
        </Stack>

        <IconButton onClick={handleCloseDrawer}>
          <CloseIcon />
        </IconButton>
      </Stack>
    </Box>

    {/* Content */}
    <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
      {selectedFeedback ? (
        <Stack spacing={3}>

          {/* Recipient Info */}
          <Box>
            <MUITypography variant="subtitle2" color="text.secondary">
              Recipient Information
            </MUITypography>

            <Divider sx={{ my: 1 }} />

            <Stack spacing={1}>
              <MUITypography>
                <strong>Phone:</strong> {selectedFeedback.phone_no}
              </MUITypography>

              <MUITypography>
                <strong>Date:</strong>{' '}
                {dayjs(selectedFeedback.created_date).format(
                  'DD MMM YYYY, hh:mm A'
                )}
              </MUITypography>
            </Stack>
          </Box>

          {/* Feedback Summary */}
          <Box>
            <MUITypography variant="subtitle2" color="text.secondary">
              Feedback Summary
            </MUITypography>

            <Divider sx={{ my: 1 }} />

            <Stack spacing={1.5}>
              {Object.entries(selectedFeedback.feedback || {}).map(
                ([key, value]) => (
                  <Box
                    key={key}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      background: '#f9f9f9',
                      border: '1px solid #eee',
                    }}
                  >
                    <MUITypography variant="body2" fontWeight={600}>
                      {key}
                    </MUITypography>

                    <MUITypography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      {value || '—'}
                    </MUITypography>
                  </Box>
                )
              )}
            </Stack>
          </Box>

          {/* Referrals */}
          {selectedFeedback.referral?.length > 0 && (
            <Box>
              <MUITypography variant="subtitle2" color="text.secondary">
                Referrals
              </MUITypography>

              <Divider sx={{ my: 1 }} />

              <Stack spacing={1}>
                {selectedFeedback.referral.map((ref, i) => (
                  <Box
                    key={i}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: '1px dashed #ccc',
                    }}
                  >
                    <MUITypography>
                      {ref.name}
                    </MUITypography>
                    <MUITypography
                      variant="body2"
                      color="text.secondary"
                    >
                      {ref.phone}
                    </MUITypography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      ) : (
        <Box textAlign="center" mt={5}>
          <MUITypography>No feedback selected</MUITypography>
        </Box>
      )}
    </Box>
  </Box>
</Drawer>
    </Box>
  );
}

export default VMFeedback;